import { BaseResponseModel } from 'src/app/models/base-response.model';
import {
  ReaCheckResponse,
  ReaImportAuditStats,
  ReaImportData,
  ReaImportLegacyCounters,
  ReaImportResult,
  ReaImportSummary,
  ReaImportWarning,
  ReaImportWarningObject,
} from 'src/app/models/fincas/rea.model';

/**
 * Extracts typed REA objects from backend responses for UI presentation.
 */
export class ReaImportDataPresenter {
  private static readonly TECHNICAL_WARNING_CODES = new Set<string>([
    'FEGA_VALIDATION_SKIPPED_IN_TEST',
    'SIEX_TEST_DATA_REQUIRED',
  ]);

  private static readonly TECHNICAL_WARNING_TEXT_PARTS = [
    'entorno test',
    'fallback test',
    'siex_bridge',
    'siexdb',
    'wkt',
    'multipolígono',
    'multipoligono',
  ];

  public static richImportResult(response: BaseResponseModel<ReaImportData> | null): ReaImportResult | null {
    const first = response?.firstOrNull();

    return this.isReaImportResult(first) ? first : null;
  }

  public static richCheckResponse(response: BaseResponseModel<ReaImportData> | null): ReaCheckResponse | null {
    const first = response?.firstOrNull();

    return this.isReaCheckResponse(first) ? first : null;
  }

  public static legacySummary(
    response: BaseResponseModel<ReaImportData> | null,
    richImportResult: ReaImportResult | null,
  ): ReaImportSummary | null {
    const richCounters = richImportResult?.legacyCounters;

    if (richCounters != null) {
      return this.toLegacySummary(richCounters);
    }

    const first = response?.firstOrNull() as unknown;

    return this.isLegacySummary(first) ? first : null;
  }

  public static auditStats(response: BaseResponseModel<ReaImportAuditStats> | null): ReaImportAuditStats | null {
    const first = response?.firstOrNull();

    return this.isReaImportAuditStats(first) ? first : null;
  }

  public static importedFincaId(result: ReaImportResult | null): number | null {
    return this.toPositiveInteger(result?.idFinca ?? result?.fincaId ?? null);
  }

  public static customerText(value: string): string {
    return this.cleanWarningText(value);
  }

  public static warningToText(warning: ReaImportWarning): string {
    if (typeof warning === 'string') {
      return this.cleanWarningText(warning);
    }

    return this.warningObjectToText(warning);
  }

  public static warningToSupportText(warning: ReaImportWarning): string {
    if (typeof warning === 'string') {
      return warning.trim();
    }

    const code = String(warning.code ?? '').trim();
    const message = String(warning.message ?? warning.description ?? '').trim();

    if (code !== '' && message !== '') {
      return `${code}: ${message}`;
    }

    return message !== '' ? message : code;
  }

  private static warningObjectToText(warning: ReaImportWarningObject): string {
    const code = String(warning.code ?? '').trim().toUpperCase();

    if (code === 'FEGA_NIF_NOT_FOUND_TEST_FALLBACK') {
      return 'No se han encontrado datos REA para este NIF.';
    }

    if (this.TECHNICAL_WARNING_CODES.has(code)) {
      return '';
    }

    return this.cleanWarningText(String(warning.message ?? warning.description ?? warning.code ?? ''));
  }

  private static cleanWarningText(value: string): string {
    const message = value.trim();

    if (message === '') {
      return '';
    }

    const normalizedMessage = message.toLowerCase();

    if (this.TECHNICAL_WARNING_TEXT_PARTS.some((technicalText) => normalizedMessage.includes(technicalText))) {
      return '';
    }

    return message;
  }

  private static toPositiveInteger(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isInteger(value) && value > 0 ? value : null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim();

    if (normalizedValue === '') {
      return null;
    }

    const numericValue = Number(normalizedValue);

    return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
  }

  private static toLegacySummary(counters: ReaImportLegacyCounters): ReaImportSummary {
    return {
      parcelasNoInsertadas: counters.parcelasNoInsertadas,
      parcelasInsertadas: counters.parcelasInsertadas,
      parcelasNoActualizadas: counters.parcelasNoActualizadas,
      parcelasActualizadas: counters.parcelasActualizadas,
    };
  }

  private static isLegacySummary(value: unknown): value is ReaImportSummary {
    return this.isRecord(value)
      && typeof value['parcelasNoInsertadas'] === 'number'
      && typeof value['parcelasInsertadas'] === 'number'
      && typeof value['parcelasNoActualizadas'] === 'number'
      && typeof value['parcelasActualizadas'] === 'number';
  }

  private static isReaImportAuditStats(value: unknown): value is ReaImportAuditStats {
    return this.isRecord(value)
      && typeof value['available'] === 'boolean'
      && typeof value['codigoRea'] === 'string'
      && typeof value['importsTotal'] === 'number'
      && this.isRecord(value['totals']);
  }

  private static isReaImportResult(value: unknown): value is ReaImportResult {
    return this.isRecord(value)
      && typeof value['importId'] === 'string'
      && typeof value['codigoRea'] === 'string'
      && this.isRecord(value['summary'])
      && Array.isArray(value['valueMessages'])
      && Array.isArray(value['warnings']);
  }

  private static isReaCheckResponse(value: unknown): value is ReaCheckResponse {
    return this.isRecord(value)
      && typeof value['nif'] === 'string'
      && typeof value['reaCount'] === 'number'
      && typeof value['requiresSelection'] === 'boolean'
      && Array.isArray(value['explotaciones']);
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }
}
